/**
 * uiAtoms.ts
 *
 * State atoms related to UI elements and interactions
 *
 * Primary responsibilities:
 * - Manage modals and dialogs
 * - Handle sidebar visibility
 * - Control toast notifications
 * - Manage loading states
 * - Handle UI theme
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import * as jotai from 'jotai'
import { getStorageForKey } from '@/lib/storage/storage-config'

// ===== Layout state atoms =====
export const showSidebarAtom = atomWithStorage<boolean>(
  'showSidebar',
  true,
  getStorageForKey('showSidebar')
)

export const sidebarWidthAtom = atomWithStorage<number>(
  'sidebarWidth',
  280,
  getStorageForKey('sidebarWidth')
)

// Collapsed (narrow) state for sidebar
export const sidebarCollapsedAtom = atomWithStorage<boolean>(
  'sidebarCollapsed',
  false,
  getStorageForKey('sidebarCollapsed')
)

export const mainContentWidthAtom = atom((get) => {
  if (!get(showSidebarAtom)) return '100%'
  return `calc(100% - ${get(sidebarWidthAtom)}px)`
})

// ===== Modal state atoms =====
export const activeModalAtom = atom<string | null>(null)

// Specific modal atoms
export const isPaywallOpenAtom = atom(false)
export const isSettingsModalOpenAtom = atom(false)
export const isShareModalOpenAtom = atom(false)
export const isExportModalOpenAtom = atom(false)
export const isDeleteConfirmModalOpenAtom = atom(false)
export const isWelcomeModalOpenAtom = atom(false)
export const isKeyboardShortcutsModalOpenAtom = atom(false)

// Nexus right-side panel (e.g. review, conversation detail, preview) width in px.
// Sidekick panel and launcher shift left by this amount so they don't overlap.
export const nexusRightPanelWidthPxAtom = atom<number>(0)

// Sidekick: active tool targeting an entity (for loader overlay on CRM rows)
export const sidekickToolActiveAtom = atom<{
  toolId: string
  params: Record<string, unknown>
  entityType?: string
  entityId?: string
} | null>(null)

// Modal data atoms
export const deleteConfirmDataAtom = atom<{
  title: string
  message: string
  onConfirm: () => void
  itemToDelete: string | null
}>({
  title: 'Delete Item',
  message: 'Are you sure you want to delete this item?',
  onConfirm: () => {},
  itemToDelete: null
})

// ===== Toast state atoms =====
export interface Toast {
  id: string
  title: string
  description?: string
  variant?: 'default' | 'success' | 'warning' | 'destructive'
  duration?: number
  message?: string
  type?: 'info' | 'success' | 'warning' | 'error'
  action?: {
    label: string
    onClick: () => void
  }
}

type ToastsGetter = {
  toastsAtom: ReturnType<typeof atom<Toast[]>>
}

export const addToast = (toast: Omit<Toast, 'id'>) => (getters: ToastsGetter) => {
  const id = Math.random().toString(36).substring(2, 9)

  return {
    toastsAtom: (prev: Toast[]) => {
      const newToast = { ...toast, id }

      // Auto-dismiss after duration (default 3 seconds)
      const duration = toast.duration || 3000
      setTimeout(() => {
        // This will need to be handled by the component
      }, duration)

      return [...prev, newToast]
    }
  }
}

export const removeToast = (id: string) => (getters: ToastsGetter) => {
  return {
    toastsAtom: (prev: Toast[]) => prev.filter((toast) => toast.id !== id)
  }
}

// ===== Loading state atoms =====
export interface LoadingState {
  isLoading: boolean
  message?: string
}

export const globalLoadingAtom = atom<LoadingState>({
  isLoading: false,
  message: ''
})

// Component-specific loading atoms
export const sidebarLoadingAtom = atom<LoadingState>({
  isLoading: false,
  message: ''
})

export const chatLoadingAtom = atom<LoadingState>({
  isLoading: false,
  message: ''
})

export const fileLoadingAtom = atom<LoadingState>({
  isLoading: false,
  message: ''
})

// ===== Theme atoms =====
type ThemeMode = 'light' | 'dark' | 'system'

export const themeModeAtom = atomWithStorage<ThemeMode>('themeMode', 'system')
export const isSystemDarkAtom = atom(false) // Is system theme dark or light?
export const effectiveThemeAtom = atom<'light' | 'dark'>((get) => {
  const themeMode = get(themeModeAtom)
  if (themeMode === 'system') {
    return get(isSystemDarkAtom) ? 'dark' : 'light'
  }
  return themeMode
})

// ===== Keyboard shortcut state =====
export const keyboardShortcutsEnabledAtom = atomWithStorage<boolean>(
  'keyboardShortcutsEnabled',
  true
)

// ===== Breakpoint atoms =====
export const isMobileAtom = atom(false)
export const isTabletAtom = atom(false)
export const isDesktopAtom = atom(false)

// ===== Helper functions =====

// Show a toast notification
export interface ShowToastOptions {
  message: string
  type?: 'info' | 'success' | 'warning' | 'error'
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

export const showToast =
  (options: ShowToastOptions) => (getters: { toastsAtom: ReturnType<typeof atom<Toast[]>> }) => {
    const id = Date.now().toString()

    return {
      toastsAtom: (prev: Toast[]) => {
        const newToast: Toast = {
          id,
          title: options.message,
          message: options.message,
          type: options.type || 'info',
          duration: options.duration || 5000,
          action: options.action
        }

        return [...prev, newToast]
      }
    }
  }

// Dismiss a toast
export const dismissToast =
  (id: string) => (getters: { toastsAtom: ReturnType<typeof atom<Toast[]>> }) => {
    return {
      toastsAtom: (prev: Toast[]) => prev.filter((toast) => toast.id !== id)
    }
  }

// Set loading state
export const setLoading =
  (isLoading: boolean, message?: string) =>
  (getters: { globalLoadingAtom: ReturnType<typeof atom<LoadingState>> }) => {
    return {
      globalLoadingAtom: { isLoading, message: message || '' }
    }
  }

// Toggle sidebar
export const toggleSidebar =
  () => (getters: { showSidebarAtom: ReturnType<typeof atom<boolean>> }) => {
    return {
      showSidebarAtom: (prev: boolean) => !prev
    }
  }

// Show a modal
export const showModal =
  (modalName: string) => (getters: { activeModalAtom: ReturnType<typeof atom<string | null>> }) => {
    return {
      activeModalAtom: modalName
    }
  }

// Hide the active modal
export const hideModal =
  () => (getters: { activeModalAtom: ReturnType<typeof atom<string | null>> }) => {
    return {
      activeModalAtom: null
    }
  }

// Set delete confirmation modal
export const showDeleteConfirmModal =
  (title: string, message: string, itemToDelete: string, onConfirm: () => void) =>
  (getters: {
    isDeleteConfirmModalOpenAtom: ReturnType<typeof atom<boolean>>
    deleteConfirmDataAtom: ReturnType<
      typeof atom<{
        title: string
        message: string
        onConfirm: () => void
        itemToDelete: string | null
      }>
    >
  }) => {
    return {
      deleteConfirmDataAtom: {
        title,
        message,
        itemToDelete,
        onConfirm
      },
      isDeleteConfirmModalOpenAtom: true
    }
  }

// ===== Delete confirmation modal =====
interface DeleteConfirmData {
  itemId: string
  itemType:
    | 'chat'
    | 'assistant'
    | 'collection'
    | 'file'
    | 'folder'
    | 'workspace'
    | 'tool'
    | 'prompt'
    | 'preset'
  itemName: string
  onConfirm: () => void
}

type DeleteConfirmGetter = {
  isDeleteConfirmModalOpenAtom: ReturnType<typeof atom<boolean>>
  deleteConfirmDataAtom: ReturnType<typeof atom<DeleteConfirmData | null>>
}

export const openDeleteConfirmModal =
  (data: DeleteConfirmData) => (getters: DeleteConfirmGetter) => {
    const { isDeleteConfirmModalOpenAtom, deleteConfirmDataAtom } = getters
    return {
      deleteConfirmDataAtom: data,
      isDeleteConfirmModalOpenAtom: true
    }
  }

export const closeDeleteConfirmModal = () => (getters: DeleteConfirmGetter) => {
  return {
    isDeleteConfirmModalOpenAtom: false,
    deleteConfirmDataAtom: null
  }
}

// Toast display state
export const toastsAtom = atom<Toast[]>([])
